'use client';

import isEqual from 'lodash.isequal';
import UnavailableDeliveryMethod from '@app/pdp/components/AddToCart/DeliveryMethods/UnavailableDeliveryMethod';
import type { AutoshipProps } from '@app/pdp/components/AddToCart/DeliveryMethods/types';
import {
    getAvailableQuantities,
    overrideMaxDisplayQuantityBySku,
    type Quantity,
} from '@app/pdp/components/AddToCart/DeliveryMethods/utils';
import AddToCartBtn from '@app/pdp/components/AddToCart/common/AddToCartBtn';
import MinimumQuantityLabel from '@app/pdp/components/AddToCart/common/MinimumQuantityLabel';
import AutoshipHeader from '@app/pdp/components/AutoshipHeader/AutoshipHeader';
import AutoshipSavingsText from '@app/pdp/components/AutoshipSavingsText/AutoshipSavingsText';
import { EstimatedTreatPoints } from '@app/pdp/components/EstimatedTreatPoints';
import Inventory from '@app/pdp/components/Inventory/Inventory';
import { DELIVERY_METHODS } from '@app/pdp/constants';
import { useProductDetails } from '@app/pdp/hooks';
import { getBasketQuantity } from '@app/pdp/utils';
import { PharmacyType } from '@libs/algolia';
import type { AddItemRequestBody } from '@libs/common-node';
import type {
    Basket,
    BasketResponse,
    OcapiError,
    ProductItem,
    AutoShipUnitOfMeasure,
} from '@libs/common-next';
import {
    useIsExperimentFullSolution,
    clsx,
    getCartCount,
    getUomByValue,
    useBasketCount,
    useCart,
    usePageLayout,
} from '@libs/common-next';
import { AUTOSHIP_CONST, getFrequencyOptions } from '@libs/shared-ui';

import { DeliveryType, getCountry, LOCALIZED } from '@libs/core';
import {
    createAutoshipBody,
    ITEM_NOT_AVAILALBLE_FAULT_TYPE,
} from '@libs/ocapi';
import type {
    AddToOrderRecurringResponse,
    AddToOrderResponse,
    PostOrdergrooveAddSubscription,
} from '@libs/order-groove';
import { createPostOrdergrooveAddSubscriptionBody } from '@libs/order-groove';
import { RadioField, SelectField } from '@petsmart-ui/sparky';
import type { FormEvent } from 'react';
import { useEffect, useState } from 'react';
import { useAddToCTCart } from '@app/pdp/components/AddToCart/hooks/useAddToCTCart';
import AutoShipRecurringShipment from './components/AutoshipRecurringShipment/AutoshipRecurringShipment';
import './Autoship.scss';

type AddToCartResponse =
    | void
    | AddToOrderResponse
    | AddToOrderRecurringResponse
    | Basket
    | OcapiError
    | null;

export default function Autoship({
    handleAddToCart,
    handleAddToExistingSubscription,
    isActive,
    isAvailable,
    isDisabledByPersonalization,
    inventory,
    inventoryQuantity,
    minimumCaseQty,
    product,
    selectVariant,
    value,
    autoship,
    isPageLoading,
    stockStatus,
}: Readonly<AutoshipProps>) {
    const {
        ocapi,
        orderGrooveData,
        setErrorAlert,
        setIsInventoryError,
        setOcapi,
        setRefreshNeededError,
        user,
        isAuthenticated,
    } = usePageLayout();
    const { cart } = useCart();
    const { handleBasketCount } = useBasketCount();
    const {
        saveSthDeliveryDetails,
        selectedProduct,
        userHasAddedToRecurringShipmentSuccessfully,
        setUserHasAddedToRecurringShipmentSuccessfully,
    } = useProductDetails();

    // constants
    const availableQuantities: Quantity[] = getAvailableQuantities(
        minimumCaseQty,
        inventoryQuantity,
        overrideMaxDisplayQuantityBySku(selectedProduct.id)
    );
    const basketId = ocapi.data?.basket?.basket_id;
    const fieldClassName = clsx(
        'autoship-fields',
        minimumCaseQty ? 'autoship-include-moq-fields' : ''
    );

    const customer = ocapi.data?.session?.customer;
    const frequencyItems = getFrequencyOptions(
        Boolean(orderGrooveData?.upcomingOrder)
    );
    const isExistingAutoshipCustomer = orderGrooveData?.upcomingOrder;
    const isPharmacyProduct =
        isEqual(selectedProduct['pharmacy-type'], PharmacyType.MED) ||
        isEqual(selectedProduct['pharmacy-type'], PharmacyType.FOOD);

    const isUpsellEligible = isExistingAutoshipCustomer && !isPharmacyProduct;
    const onHandQuantity = getBasketQuantity(
        ocapi.data ?? undefined,
        inventory.ats,
        product.objectID
    );
    const startingQuantity = minimumCaseQty || 1;

    // useStates
    const [addedToCart, setAddedToCart] = useState(false);
    const [isOgProcessing, setIsOgProcessing] = useState(false);
    const [isCTCartLoading, setIsCTCartLoading] = useState(false);

    const country = getCountry();
    const isExperimentFullSolution = useIsExperimentFullSolution();

    const { addToCTCart, isLoading: isCTLoading } = useAddToCTCart();
    useEffect(() => {
        setIsCTCartLoading(isExperimentFullSolution && isCTLoading);
    }, [isExperimentFullSolution, isCTCartLoading, isCTLoading]);

    const [selectedFrequency, setSelectedFrequency] = useState(
        AUTOSHIP_CONST.DEFAULT_OPTION
    );

    const [selectedQty, setSelectedQty] = useState(startingQuantity);

    const pharmacyOutOfStock =
        selectedProduct.customPharmacyOOS === true ||
        selectedProduct.customPharmacyOOS === 'TRUE' ||
        selectedProduct.customPharmacyOOS === 'true';

    // TODO: Add hook post M4 for in-stock and OOS
    const inStock =
        !pharmacyOutOfStock &&
        onHandQuantity > 0 &&
        inventory.orderable &&
        !!availableQuantities?.length;

    // helper functions
    const getButtonText = () => {
        if (isUpsellEligible) {
            return addedToCart
                ? AUTOSHIP_CONST.ADDED_TO_AUTOSHIP_TEXT
                : AUTOSHIP_CONST.ADD_TO_AUTOSHIP_TEXT;
        }
        return addedToCart ? DELIVERY_METHODS.ADDED_TO_CART : '';
    };

    const onQuantityChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const qty = event?.target.value;

        if (qty) {
            if (!isExperimentFullSolution) {
                setOcapi(oldOcapi => {
                    if (oldOcapi.data) {
                        return {
                            ...oldOcapi,
                            data: {
                                ...oldOcapi.data,
                                basket: {
                                    ...oldOcapi.data?.basket,
                                    order_total: Number(qty),
                                },
                            },
                        };
                    }
                    return oldOcapi;
                });
            }
            setSelectedQty(parseInt(qty, 10));
        }
    };

    const onFrequencyChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const frequency = event?.target.value;

        if (frequency) {
            setSelectedFrequency(frequency);
        }
    };

    useEffect(() => {
        /*
         * This effect will set the selected quantity of items that have a minimum order quantity
         * to be whatever the MOQ happens to be.
         */

        const defaultQuantity = Number(
            availableQuantities[0] ? availableQuantities[0].value : 1
        );

        if (defaultQuantity && startingQuantity !== defaultQuantity) {
            setSelectedQty(defaultQuantity);
        }
    }, [availableQuantities, startingQuantity]);

    const handleResponseUIUpdates = (response: AddToCartResponse) => {
        // for successful OrderGroove add-to-existing-subscription call, we get a response with public_id attribute which is specific for OG
        if (response && Object.hasOwn(response, 'public_id')) {
            setTimeout(() => {
                setUserHasAddedToRecurringShipmentSuccessfully(true);
            }, 1000);
        }
        // If the code reaches this part, it means it's either an SFCC cart or a CTCart. Since CTCart handles basket updates under the hood, we only check for SFCC and update the basket count accordingly.
        // It's wrapped in an else-if because both cases shouldn't work simultaneously.
        else if (!isExperimentFullSolution) {
            const cartCount = getCartCount(response as BasketResponse);
            if (cartCount && !isUpsellEligible) {
                handleBasketCount(cartCount);
            }
        }
    };

    const getOGCartBody = (
        postOrderAddSubscriptionBody: PostOrdergrooveAddSubscription,
        item: Partial<ProductItem>
    ) => (isUpsellEligible ? postOrderAddSubscriptionBody : item);

    const handleOGCartErrors = (
        response: AddToCartResponse,
        item: Partial<ProductItem>,
        postOrderAddSubscriptionBody: PostOrdergrooveAddSubscription
    ) => {
        if (
            (response as OcapiError)?.fault?.type ===
            ITEM_NOT_AVAILALBLE_FAULT_TYPE
        ) {
            console.error(
                `Inventory mismatch error. SKU: ${item?.product_id}, Quantity: ${item?.quantity}, Inventory List: ${item?.inventory_id}`
            );
            setIsInventoryError(true);
            setErrorAlert(true);
            return true;
        }

        if (!isExperimentFullSolution && (!response || 'fault' in response)) {
            setIsOgProcessing(false);
            console.error(
                `AutoShip: failed or missing response from adding autoship item to cart. Request parameters: ${JSON.stringify(
                    getOGCartBody(postOrderAddSubscriptionBody, item)
                )}`
            );
            setErrorAlert(true);
            return true;
        }

        return false;
    };

    const addToAutoShipProgram = async (
        ctCardItem: Falsy | AddItemRequestBody,
        item: Partial<ProductItem>,
        postOrderAddSubscriptionBody: PostOrdergrooveAddSubscription
    ) => {
        if (!isUpsellEligible) {
            return ctCardItem
                ? await addToCTCart(ctCardItem, true)
                : await handleAddToCart([item], basketId as string);
        }
        return await handleAddToExistingSubscription(
            postOrderAddSubscriptionBody
        );
    };

    async function handleFormSubmit(event: FormEvent<HTMLFormElement>) {
        event.preventDefault();

        if (!isAvailable || isCTCartLoading || isPageLoading) {
            return;
        }

        setIsOgProcessing(true);

        // selectedFrequency: 3_3 -> [3, 3] -> amount: 3, measure: 3
        const [amount, measure] = selectedFrequency.split('_');
        const frequencyAmount = parseInt(amount, 10);
        const unitOfMeasure: AutoShipUnitOfMeasure = getUomByValue(
            parseInt(measure, 10)
        ); // Days = 1, Weeks = 2, Months = 3,

        const productId = selectVariant?.id?.toString() || product.objectID;

        const item = createAutoshipBody({
            productId,
            quantity: selectedQty,
            frequencyAmount,
            unitOfMeasure,
        });
        const cartBasketId = cart?.basketId ?? '';
        const postOrderAddSubscriptionBody =
            createPostOrdergrooveAddSubscriptionBody({
                productId,
                quantity: selectedQty,
                frequencyAmount,
                unitOfMeasure,
                basketId: !isExperimentFullSolution
                    ? (basketId as string)
                    : cartBasketId,
                publicId: orderGrooveData?.upcomingOrder.publicId as string,
                customerId:
                    isAuthenticated === 'velocity'
                        ? user?.sid
                        : customer?.c_janrainIdentifier, // as the sid of new implemented user session is the same as c_janrainIdentifier, we're checking if user logged in with new experience passing sid if not the legacy identifier
                isOneTimeOrder:
                    selectedFrequency === AUTOSHIP_CONST.ONE_TIME_VALUE,
            }) as PostOrdergrooveAddSubscription;

        // Stay non-blocking as we don't need to wait for this to complete.
        if (!isExperimentFullSolution) {
            saveSthDeliveryDetails({
                inventoryListId: LOCALIZED.webInventory,
                postalCode: LOCALIZED.staticPostalCode,
                productId,
            }).then(result => {
                if (!result) {
                    // Making this obvious visually as it's a rare and important error.
                    // We are not sure how to deal it yet, but we shall see in upcoming tests if this error
                    // even happens.
                    setErrorAlert(true);
                    console.error('Failed to set postal code.');
                }
            });
        }

        let response;
        try {
            const ctCardItem: Falsy | AddItemRequestBody =
                isExperimentFullSolution && {
                    country,
                    sku: productId,
                    quantity: selectedQty,
                    fulfillmentMethod: DeliveryType.AUTOSHIP,
                    autoshipFrequency: `${frequencyAmount}`,
                    autoshipFrequencyUnitOfMeasure: `${unitOfMeasure}`,
                };
            response = await addToAutoShipProgram(
                ctCardItem,
                item,
                postOrderAddSubscriptionBody
            );

            const shouldExit = handleOGCartErrors(
                response,
                item,
                postOrderAddSubscriptionBody
            );
            if (shouldExit) {
                return;
            }

            setIsOgProcessing(false);
            setAddedToCart(true);
        } catch (e) {
            setRefreshNeededError(true);
            console.error(
                `AutoShip: Error while attempting add autoship item to cart. Request parameters: ${JSON.stringify(
                    getOGCartBody(postOrderAddSubscriptionBody, item)
                )}`,
                e
            );
            setErrorAlert(true);
            setIsOgProcessing(false);
            return;
        }

        handleResponseUIUpdates(response);
    }

    if (isPageLoading) {
        return (
            <UnavailableDeliveryMethod deliveryMethod="Autoship" isLoading />
        );
    }

    if (isDisabledByPersonalization || !isAvailable) {
        return <UnavailableDeliveryMethod deliveryMethod="Autoship" />;
    }

    return (
        <RadioField.Item
            className="autoship"
            value={value}
            text={
                <AutoshipHeader
                    productId={product.id.toString()}
                    userHasSubscriptions={
                        !!orderGrooveData?.upcomingOrder?.displayShipmentDate
                    }
                    eligiblePromotions={product?.eligiblePromotions}
                />
            }
            itemAfter={
                <>
                    <AutoshipSavingsText
                        isActive={isActive}
                        isAvailable
                        autoship={autoship}
                        displayShipmentDate={
                            isUpsellEligible
                                ? orderGrooveData?.upcomingOrder
                                      ?.displayShipmentDate
                                : undefined
                        }
                    />
                    <div className="stock-status">
                        {stockStatus !== 'Pending' ? (
                            <Inventory
                                isAvailable={isAvailable && inStock}
                                availableToSell={onHandQuantity}
                            />
                        ) : null}
                    </div>
                </>
            }
            hiddenContent={
                <form onSubmit={handleFormSubmit}>
                    {isAvailable && inStock && (
                        <div data-testid="in-stock-fields">
                            {userHasAddedToRecurringShipmentSuccessfully ? (
                                <AutoShipRecurringShipment
                                    displayShipmentDate={
                                        orderGrooveData?.upcomingOrder
                                            ?.displayShipmentDate
                                    }
                                />
                            ) : (
                                <>
                                    <div className={fieldClassName}>
                                        <SelectField
                                            label="Frequency"
                                            data-testid="select-frequency"
                                            value={selectedFrequency}
                                            items={frequencyItems}
                                            onChange={onFrequencyChange}
                                        />

                                        <MinimumQuantityLabel
                                            minimumCaseQty={minimumCaseQty}
                                            className={`${fieldClassName}--label-wrapper`}
                                        />

                                        <SelectField
                                            value={selectedQty}
                                            onChange={onQuantityChange}
                                            data-testid="select-quantity"
                                            label="Quantity"
                                            items={availableQuantities}
                                            disabled={!inStock}
                                        />
                                    </div>
                                    {availableQuantities.length > 0 && (
                                        <EstimatedTreatPoints
                                            qty={selectedQty}
                                        />
                                    )}

                                    <br />
                                    <AddToCartBtn
                                        text={getButtonText()}
                                        className={clsx(
                                            addedToCart &&
                                                'add-to-cart-button add-to-cart-button__green'
                                        )}
                                        setAddedToCart={setAddedToCart}
                                        addedToCart={addedToCart}
                                        isLoading={
                                            isOgProcessing ||
                                            isCTCartLoading ||
                                            isPageLoading
                                        }
                                    />
                                </>
                            )}
                        </div>
                    )}
                </form>
            }
        />
    );
}
